import type { BusinessSubscriptionAccess } from '@neogeniux/api-contracts';
import {
  AddonInfo,
  PrepaidPackPreview,
  PrepaidPackPurchaseResponse,
  BusinessSubscription,
  CreateSubscriptionPaymentDto,
  PaginatedResponse,
  PaymentFailureStatus,
  PaypalCaptureOrderDto,
  PaypalCreateOrderDto,
  PaypalCreateOrderResponseDto,
  PaypalSubscriptionInput,
  PaypalSubscriptionResponse,
  PlanUsage,
  PurchaseAddonDto,
  PurchaseAddonResponse,
  PurchaseAddonPreviewResponse,
  RetryPaymentResponse,
  StripeDefaultPaymentMethodSummaryDto,
  StripePayDto,
  StripeSetDefaultPaymentMethodDto,
  StripeSetupIntentResponseDto,
  SubscriptionPayment,
  SubscriptionPaymentsParams,
  TrialStatus,
  UpdateBusinessSubscriptionDto,
  UpdateMySubscriptionDto,
  UpdateMySubscriptionResponse,
} from '@app/shared/models/subscription.model';
import { apiService } from '@app/shared/services/api.service';

export interface MySubscriptionItem {
  id: string;
  businessId: string;
  subscriptionId: string;
  itemKind:
    | 'product'
    | 'module'
    | 'feature'
    | 'addon'
    | 'quota'
    | 'limit'
    | 'plan_template'
    | 'bundle';
  itemId: string;
  itemCode: string;
  quantity: number;
  isIncluded: boolean;
  /**
   * Bundle-first billing switch. Plan_template rows bill (isBillable=
   * true, isIncluded=false); components inherit entitlement but bill
   * zero (isBillable=false, isIncluded=true).
   */
  isBillable: boolean;
  grandfathered: boolean;
  source: string;
  effectiveFrom: string | null;
  effectiveUntil: string | null;
  /**
   * True when this cortesía / complimentary row already ships with
   * the business's paid catalog plan template. Renders a "No
   * aplicada — ya incluida en el plan" badge on /account. Absent on
   * legacy responses; treat as false.
   */
  isRedundantWithPlan?: boolean;
}

export const subscriptionService = {
  getMySubscription: (): Promise<BusinessSubscription> =>
    apiService.getInstance().get('/subscription/me'),

  /**
   * Active business_subscription_item rows for the caller. Used by
   * the /account available-addons section to filter "Agregar" off
   * items the operator already owns.
   */
  getMySubscriptionItems: (): Promise<MySubscriptionItem[]> =>
    apiService.getInstance().get('/subscription/me/items'),

  getMyTrialStatus: (): Promise<TrialStatus> =>
    apiService.getInstance().get('/subscription/me/trial-status'),

  getMyAccessStatus: (): Promise<BusinessSubscriptionAccess> =>
    apiService.getInstance().get('/subscription/me/access'),

  getMyPlanUsage: (branchOfficeId?: string): Promise<PlanUsage> =>
    apiService.getInstance().get('/subscription/me/plan-usage', {
      params: branchOfficeId ? { branchOfficeId } : undefined,
    }),

  updateMySubscription: (
    data: UpdateMySubscriptionDto
  ): Promise<UpdateMySubscriptionResponse> =>
    apiService.getInstance().patch('/subscription/me', data),

  createMyStripeSetupIntent: (): Promise<StripeSetupIntentResponseDto> =>
    apiService.getInstance().post('/subscription/me/stripe/setup-intent', {}),

  getMyStripeDefaultPaymentMethodSummary:
    (): Promise<StripeDefaultPaymentMethodSummaryDto | null> =>
      apiService
        .getInstance()
        .get('/subscription/me/stripe/default-payment-method'),

  setMyStripeDefaultPaymentMethod: (
    data: StripeSetDefaultPaymentMethodDto
  ): Promise<BusinessSubscription> =>
    apiService
      .getInstance()
      .post('/subscription/me/stripe/default-payment-method', data),

  payMySubscriptionWithStripe: (
    data: StripePayDto
  ): Promise<SubscriptionPayment> =>
    apiService.getInstance().post('/subscription/me/stripe/pay', data),

  createMyPaypalOrder: (
    data: PaypalCreateOrderDto
  ): Promise<PaypalCreateOrderResponseDto> =>
    apiService.getInstance().post('/subscription/me/paypal/order', data),

  captureMyPaypalOrder: (
    data: PaypalCaptureOrderDto
  ): Promise<SubscriptionPayment> =>
    apiService.getInstance().post('/subscription/me/paypal/capture', data),

  connectMyPaypal: (): Promise<PaypalCreateOrderResponseDto> =>
    apiService.getInstance().post('/subscription/me/paypal/connect', {}),

  confirmMyPaypalConnection: (
    orderId: string
  ): Promise<{ connected: boolean }> =>
    apiService
      .getInstance()
      .post('/subscription/me/paypal/connect/confirm', { orderId }),

  getMyPayments: (
    params: SubscriptionPaymentsParams
  ): Promise<PaginatedResponse<SubscriptionPayment>> =>
    apiService.getInstance().get('/subscription/me/payments', { params }),

  getMyPaymentStatus: (): Promise<PaymentFailureStatus> =>
    apiService.getInstance().get('/subscription/me/payment-status'),

  retryMyPayment: (): Promise<RetryPaymentResponse> =>
    apiService.getInstance().post('/subscription/me/retry-payment', {}),

  createMyPaypalSubscription: (
    data: PaypalSubscriptionInput
  ): Promise<PaypalSubscriptionResponse> =>
    apiService.getInstance().post('/subscription/me/paypal/subscription', data),

  activateMyPaypalSubscription: (
    paypalSubscriptionId: string
  ): Promise<BusinessSubscription> =>
    apiService
      .getInstance()
      .post('/subscription/me/paypal/subscription/activate', {
        paypalSubscriptionId,
      }),

  repairMyPaypalPayments: (): Promise<{ repaired: number }> =>
    apiService
      .getInstance()
      .post('/subscription/me/paypal/repair-payments', {}),

  getBusinessSubscription: (
    businessId: string
  ): Promise<BusinessSubscription> =>
    apiService.getInstance().get(`/subscription/business/${businessId}`),

  createBusinessStripeSetupIntent: (
    businessId: string
  ): Promise<StripeSetupIntentResponseDto> =>
    apiService
      .getInstance()
      .post(`/subscription/business/${businessId}/stripe/setup-intent`, {}),

  getBusinessStripeDefaultPaymentMethodSummary: (
    businessId: string
  ): Promise<StripeDefaultPaymentMethodSummaryDto | null> =>
    apiService
      .getInstance()
      .get(
        `/subscription/business/${businessId}/stripe/default-payment-method`
      ),

  setBusinessStripeDefaultPaymentMethod: (
    businessId: string,
    data: StripeSetDefaultPaymentMethodDto
  ): Promise<BusinessSubscription> =>
    apiService
      .getInstance()
      .post(
        `/subscription/business/${businessId}/stripe/default-payment-method`,
        data
      ),

  payBusinessSubscriptionWithStripe: (
    businessId: string,
    data: StripePayDto
  ): Promise<SubscriptionPayment> =>
    apiService
      .getInstance()
      .post(`/subscription/business/${businessId}/stripe/pay`, data),

  createBusinessPaypalOrder: (
    businessId: string,
    data: PaypalCreateOrderDto
  ): Promise<PaypalCreateOrderResponseDto> =>
    apiService
      .getInstance()
      .post(`/subscription/business/${businessId}/paypal/order`, data),

  captureBusinessPaypalOrder: (
    businessId: string,
    data: PaypalCaptureOrderDto
  ): Promise<SubscriptionPayment> =>
    apiService
      .getInstance()
      .post(`/subscription/business/${businessId}/paypal/capture`, data),

  updateBusinessSubscription: (
    businessId: string,
    data: UpdateBusinessSubscriptionDto
  ): Promise<BusinessSubscription> =>
    apiService
      .getInstance()
      .patch(`/subscription/business/${businessId}`, data),

  getBusinessPayments: (
    businessId: string,
    params: SubscriptionPaymentsParams
  ): Promise<PaginatedResponse<SubscriptionPayment>> =>
    apiService
      .getInstance()
      .get(`/subscription/business/${businessId}/payments`, { params }),

  createBusinessPayment: (
    businessId: string,
    data: CreateSubscriptionPaymentDto
  ): Promise<SubscriptionPayment> =>
    apiService
      .getInstance()
      .post(`/subscription/business/${businessId}/payments`, data),

  cancelMySubscription: (): Promise<{
    message: string;
    cancelledImmediately: boolean;
    providerCancellationFailed: boolean;
  }> => apiService.getInstance().post('/subscription/me/cancel', {}),

  cancelMySubscriptionToFree: (): Promise<{ message: string }> =>
    apiService.getInstance().patch('/subscription/me', { plan: 'FREE' }),

  getMyAddons: (): Promise<AddonInfo> =>
    apiService.getInstance().get('/subscription/me/addons'),

  purchaseMyAddon: (data: PurchaseAddonDto): Promise<PurchaseAddonResponse> =>
    apiService.getInstance().post('/subscription/me/addons/purchase', data),

  previewMyPrepaidPack: (quantity: number): Promise<PrepaidPackPreview> =>
    apiService
      .getInstance()
      .get(`/subscription/me/addons/prepaid-pack/preview?quantity=${quantity}`),

  purchaseMyPrepaidPack: (data: {
    quantity: number;
    idempotencyKey: string;
  }): Promise<PrepaidPackPurchaseResponse> =>
    apiService
      .getInstance()
      .post('/subscription/me/addons/prepaid-pack/purchase', data),

  previewPurchaseMyAddon: (
    data: PurchaseAddonDto
  ): Promise<PurchaseAddonPreviewResponse> =>
    apiService
      .getInstance()
      .post('/subscription/me/addons/purchase/preview', data),

  capturePaypalAddonOrder: (orderId: string): Promise<PurchaseAddonResponse> =>
    apiService
      .getInstance()
      .post('/subscription/me/addons/paypal/capture', { orderId }),

  previewReduceMyAddon: (data: {
    addonType: ReducibleAddonType;
    slotsToDrop: number;
  }): Promise<ReduceAddonPreview> =>
    apiService
      .getInstance()
      .post('/subscription/me/addons/reduce/preview', data),

  reduceMyAddon: (data: {
    addonType: ReducibleAddonType;
    slotsToDrop: number;
  }): Promise<ReduceAddonResult> =>
    apiService.getInstance().post('/subscription/me/addons/reduce', data),

  getBusinessAddons: (businessId: string): Promise<AddonInfo> =>
    apiService.getInstance().get(`/subscription/business/${businessId}/addons`),
};

export type ReducibleAddonType =
  | 'extra_branch'
  | 'extra_user'
  | 'extra_warehouse'
  | 'extra_product'
  | 'extra_kds_screen'
  | 'extra_ecf';

export interface ReduceAddonPreview {
  currentMonthly: number;
  nextMonthly: number;
  delta: number;
  effectiveAt: string;
  currency: string;
  addonType: string;
  slotsToDrop: number;
}

export interface ReduceAddonResult {
  scheduledSlots: number;
  effectiveAt: string;
  addonType: string;
}
